/**
 * Real staff sign-in: MSAL redirect flow, configured entirely from the server.
 *
 * **Nothing about the tenant is compiled into this bundle.** The client id, the
 * authority and the scope all arrive from `GET /auth/config` at runtime, which is
 * why this module takes an `AuthConfig` rather than reading `import.meta.env`. The
 * reason is not secrecy — a public client's ids are public — it is that the
 * bundle and the backend must never be able to disagree about which registration
 * they are using: the API validates `aud` against its own `AUTH_API_CLIENT_ID`, so
 * a bundle carrying a stale id would produce tokens the API refuses, and the only
 * clue would be a 401 with a confusing message. One source, one answer.
 *
 * **Redirect flow, not popup.** An iPad in a walk-in is one browser window and
 * Safari's popup blocking is not something to negotiate with mid-count. Redirect
 * also survives the installed standalone app, where a popup has nowhere to go.
 *
 * ⚠ **`redirectUri` is `window.location.origin` with NO trailing slash.** Entra
 * matches redirect URIs by exact string, and the registration's SPA URIs are
 * recorded as `https://inventory.cubanbread.com` and `http://localhost:5173`
 * (STATUS.md, "The Entra registration blocker — CLOSED"). `origin` produces
 * exactly those. Appending `/` would earn an `AADSTS50011` error page instead of
 * a login form, which is a five-minute mystery every time.
 *
 * ⚠ **msal-browser v5 moved two settings** this module would otherwise have set
 * in its `Configuration`: `navigateToLoginRequestUrl` is now an argument to
 * `handleRedirectPromise` (see `completeRedirect`), and `storeAuthStateInCookie`
 * is gone entirely. Neither is a behaviour change here — the first is passed
 * explicitly at the call, and the second was only ever for browsers this app does
 * not target — but a v4 example pasted in from the internet will not compile, and
 * that is worth knowing before assuming the file is wrong.
 *
 * **Silent renewal, never a silent redirect.** `acquireTokenSilent` runs before
 * every request and is what keeps a Friday count from being interrupted. When it
 * fails because interaction is genuinely required, this module says so and returns
 * no token — it does **not** call `loginRedirect` on its own. A redirect fired
 * from inside a fetch would throw away whatever screen the manager was on, and
 * (worse) could loop. The app puts them back on S1 with the reason and a button,
 * which is one tap and no lost work: drafts are in IndexedDB either way.
 */
import {
  InteractionRequiredAuthError,
  LogLevel,
  createStandardPublicClientApplication,
  type AccountInfo,
  type Configuration,
  type IPublicClientApplication,
} from "@azure/msal-browser";

import type { AuthConfig } from "../api/types";

/**
 * `localStorage`. **RULED by Copeland, 2026-08-24** — not a preference to revisit.
 *
 * MSAL's default is `sessionStorage`, which dies when the tab or the installed app
 * is closed. On an iPad that means a manager who backgrounds the app long enough
 * for iOS to reclaim it signs in again — **in a walk-in, on bakery WiFi,
 * mid-count, which is exactly the failure this app exists to prevent.**
 * `localStorage` lets the same device pick the session back up silently, which is
 * the behaviour § 4.8's device checklist is about.
 *
 * The cost — a shared iPad stays signed in until somebody signs out — was put to
 * Copeland and **accepted, mitigated by Sign out in the header on every screen**
 * (`SessionBar.tsx`). That is why the control lives in the header rather than in
 * diagnostics: moving it would remove the mitigation this ruling depends on.
 *
 * ⚠ **This constant is the single revert point.** If the trade is ever refused,
 * changing this one string to `"sessionStorage"` is the entire change — nothing
 * else reads the cache location, and `buildMsalConfig` below is its only consumer.
 * It exists as a named constant for that reason. `tests/entraSignIn.test.tsx` pins
 * the value, so a change is deliberate and announces itself.
 */
export const MSAL_CACHE_LOCATION = "localStorage" as const;

/** What a silent token acquisition produced: a token, or the reason there is none. */
export interface TokenResult {
  token: string | null;
  /** Manager-readable, and never invented — MSAL's own words when it has some. */
  reason: string | null;
  /** True when only a fresh interactive sign-in can fix it. */
  interactionRequired: boolean;
}

export function buildMsalConfig(config: AuthConfig): Configuration {
  if (!config.client_id) {
    throw new Error(
      "/auth/config returned no client_id, so MSAL cannot be initialised. The " +
        "backend is in entra mode without AUTH_API_CLIENT_ID set.",
    );
  }
  return {
    auth: {
      clientId: config.client_id,
      // The server's authority, which carries the tenant. Single-tenant by
      // registration; `common` would let another directory's account reach the
      // login form and be refused later, which is a worse error than not
      // offering it.
      authority:
        config.authority ??
        `https://login.microsoftonline.com/${config.tenant_id ?? "organizations"}`,
      // See the ⚠ in the module docstring: exact-match, no trailing slash.
      redirectUri: window.location.origin,
      postLogoutRedirectUri: window.location.origin,
    },
    cache: {
      cacheLocation: MSAL_CACHE_LOCATION,
    },
    system: {
      loggerOptions: {
        // Errors only, and never PII. Enough to debug a sign-in from Safari's
        // console on the iPad run without logging anybody's token or UPN.
        logLevel: LogLevel.Error,
        piiLoggingEnabled: false,
        loggerCallback: (level, message, containsPii) => {
          if (containsPii) return;
          if (level === LogLevel.Error) console.error(`[msal] ${message}`);
        },
      },
    },
  };
}

/** The scopes to ask for: the server's, or none, which is a misconfiguration. */
export function msalScopes(config: AuthConfig): string[] {
  return config.scopes.length > 0 ? [...config.scopes] : [];
}

/**
 * Whether a failed silent acquisition means "ask the manager to sign in again".
 *
 * Three ways of asking, because MSAL reports this three ways depending on where
 * it gave up: the typed error, its name (which survives being serialised across
 * an iframe), and the error codes it uses when there is no account in the cache
 * at all. Missing one of them presents as a screen with no token and no
 * explanation, which is the failure this whole path exists to avoid.
 */
export function needsInteraction(error: unknown): boolean {
  if (error instanceof InteractionRequiredAuthError) return true;
  const named = error as { name?: string; errorCode?: string } | null;
  if (named?.name === "InteractionRequiredAuthError") return true;
  return Boolean(
    named?.errorCode &&
      [
        "interaction_required",
        "login_required",
        "consent_required",
        "no_account_error",
        "no_tokens_found",
        "no_account_in_silent_request",
      ].includes(named.errorCode),
  );
}

function messageFor(error: unknown): string {
  const err = error as { errorMessage?: string; message?: string } | null;
  return err?.errorMessage || err?.message || String(error);
}

/**
 * A signed-in (or signable-in) Entra session.
 *
 * Wraps the MSAL instance rather than exposing it, so the rest of the app deals
 * in "give me a token" and "sign out" and never in MSAL's vocabulary. That keeps
 * the token path — the thing every request depends on — in one readable place.
 */
export class EntraAuth {
  constructor(
    readonly instance: IPublicClientApplication,
    readonly scopes: string[],
  ) {}

  /** The signed-in account, if there is one. */
  account(): AccountInfo | null {
    return this.instance.getActiveAccount() ?? this.instance.getAllAccounts()[0] ?? null;
  }

  /**
   * The UPN, lower-cased to match `access.yaml`'s keys.
   *
   * `username` is what MSAL calls `preferred_username`, which is the same claim
   * the backend authorises on (`auth/entra.py`). Using anything else here —
   * `name`, say — would show a manager a display name while the server made its
   * decision on something different.
   */
  upn(): string | null {
    const account = this.account();
    return account?.username ? account.username.toLowerCase() : null;
  }

  displayName(): string | null {
    const account = this.account();
    return account?.name ?? account?.username ?? null;
  }

  /**
   * Finish a redirect that has just come back, and adopt its account.
   *
   * Safe to call when there is no redirect in flight — MSAL resolves `null` — so
   * this runs on every boot rather than being conditional on inspecting the URL
   * ourselves.
   */
  async completeRedirect(): Promise<void> {
    // `navigateToLoginRequestUrl` moved from the Configuration to this call in
    // msal-browser v5 (it lives in `HandleRedirectPromiseOptions` now, and
    // defaults to true). Passed explicitly because the behaviour is one we rely
    // on and would not want a default to change quietly: sign-in started from a
    // bookmarked session URL comes back to **that** URL, not to the area list.
    const result = await this.instance.handleRedirectPromise({
      navigateToLoginRequestUrl: true,
    });
    const account = result?.account ?? this.account();
    if (account) this.instance.setActiveAccount(account);
  }

  /**
   * Call `listener` whenever MSAL's idea of who is signed in changes.
   *
   * This is what `@azure/msal-react`'s `MsalProvider` does internally, and doing
   * it here rather than mounting that provider is a **deliberate, tested-into**
   * decision — put to Copeland and **APPROVED 2026-08-24** as requirement over
   * named library, with `@azure/msal-react` uninstalled rather than left installed
   * and unused. The deciding fact is data loss, not tidiness:
   *
   * - `MsalProvider` needs the instance as a prop, and the instance only exists
   *   after `/auth/config` has answered and MSAL has initialised. Mounting it
   *   then changes the shape of the React tree, which **unmounts and remounts
   *   every screen below it** — observed while building D7, as a sign-in button
   *   whose DOM node was replaced underneath a test that was holding it. Mid-count
   *   that would discard whatever a manager had typed but not yet synced.
   * - The documented placeholder for "no instance yet",
   *   `stubbedPublicClientApplication`, cannot be used to keep the tree stable:
   *   its `getLogger()` **throws**, and `MsalProvider` calls exactly that during
   *   render.
   *
   * So the subscription lives here, the tree never changes shape, and the app
   * still learns about a redirect completing, a token being acquired or a
   * sign-out happening in another tab — which is the whole reason to want the
   * provider in the first place.
   */
  onAccountChange(listener: (account: AccountInfo | null) => void): () => void {
    // No event filter: every MSAL event is cheap to answer, and enumerating the
    // interesting ones is how a future MSAL version quietly stops notifying.
    const callbackId = this.instance.addEventCallback(() => {
      const account = this.account();
      if (account && !this.instance.getActiveAccount()) {
        this.instance.setActiveAccount(account);
      }
      listener(account);
    });
    return () => {
      if (callbackId) this.instance.removeEventCallback(callbackId);
    };
  }

  async signIn(): Promise<void> {
    await this.instance.loginRedirect({
      scopes: this.scopes,
      // A manager who signed out and back in on a shared iPad should be able to
      // pick a different account, so no `loginHint` and no `prompt: "none"`.
    });
  }

  async signOut(): Promise<void> {
    const account = this.account();
    await this.instance.logoutRedirect({
      account: account ?? undefined,
      postLogoutRedirectUri: window.location.origin,
    });
  }

  /**
   * A token for the API, silently if at all possible.
   *
   * Runs per request, which sounds expensive and is not: MSAL returns the cached
   * access token until it is close to expiry and only then spends a round trip
   * renewing it. The alternative — caching a token in this app — would mean
   * writing an expiry check that MSAL already has.
   */
  async token(): Promise<TokenResult> {
    const account = this.account();
    if (!account) {
      return {
        token: null,
        reason: "No signed-in account on this device.",
        interactionRequired: true,
      };
    }
    try {
      const result = await this.instance.acquireTokenSilent({ scopes: this.scopes, account });
      return { token: result.accessToken, reason: null, interactionRequired: false };
    } catch (error) {
      return {
        token: null,
        reason: messageFor(error),
        interactionRequired: needsInteraction(error),
      };
    }
  }
}

/**
 * One MSAL instance per page load, keyed by client id.
 *
 * React's StrictMode runs effects twice in development and `/auth/config` can
 * settle more than once, so "create the instance in an effect" has to be
 * idempotent or two instances race over the same `localStorage` keys and one
 * loses the redirect. The cache is a promise, not an instance, so a second caller
 * arriving mid-initialisation waits for the first rather than starting another.
 */
let cached: { clientId: string; promise: Promise<EntraAuth> } | null = null;

export async function getEntraAuth(config: AuthConfig): Promise<EntraAuth> {
  const clientId = config.client_id;
  if (!clientId) throw new Error("cannot initialise MSAL without a client id");
  if (cached && cached.clientId === clientId) return cached.promise;

  const promise = (async () => {
    // `createStandardPublicClientApplication` constructs *and* initialises, so
    // there is no window in which a caller can hold an uninitialised instance
    // and get a confusing "initialize has not been called" error.
    const instance = await createStandardPublicClientApplication(buildMsalConfig(config));
    const auth = new EntraAuth(instance, msalScopes(config));
    await auth.completeRedirect();
    return auth;
  })();

  cached = { clientId, promise };
  // A failed initialisation must not be cached: the next attempt (a reload, or a
  // backend that has since been fixed) should try again rather than replay the
  // same rejection forever.
  promise.catch(() => {
    if (cached?.promise === promise) cached = null;
  });
  return promise;
}

/**
 * Drop the cached instance.
 *
 * Exported for tests, which build a fresh fake per case. Production has no need
 * for it — the client id cannot change without a page load, and a page load
 * clears this module anyway.
 */
export function resetEntraAuth(): void {
  cached = null;
}
